import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useModalStore } from '@/stores/modal'
import { useSettingsStore } from '@/stores/settings'
import { useSystemStore } from '@/stores/system'
import { useToastsStore } from '@/stores/toasts'
import en from '@/locales/en.json'
import de from '@/locales/de.json'
import router from '@/router'
import App from '@/layouts/App.vue'
import { setApiHandlers } from '@/services/api'
import { setDateTimeLocale } from '@/services/date-time'
import { getApiFieldErrors } from '@/services/api-error'
import { registerToastStore, toast } from '@/services/toast'
import ConfirmPasswordModal from '@/components/modals/ConfirmPasswordModal.vue'
import { LockClosedIcon } from '@heroicons/vue/24/outline'
import UiComponents from '@/components/ui/index.js';
import '../css/app.css'

const pinia = createPinia()
const authStore = useAuthStore(pinia)
const modalStore = useModalStore(pinia)
const systemStore = useSystemStore(pinia)
const settingsStore = useSettingsStore(pinia)
const toastsStore = useToastsStore(pinia)
const localeMessages = {
  en,
  de,
}

const resolveTranslation = (locale, key) => {
  const resolvedLocale = Object.prototype.hasOwnProperty.call(localeMessages, locale)
    ? locale
    : 'en'

  const keyParts = String(key).split('.')
  const readMessage = (messages) => keyParts.reduce((value, keyPart) => {
    if (value && typeof value === 'object') {
      return value[keyPart]
    }

    return undefined
  }, messages)

  const localizedMessage = readMessage(localeMessages[resolvedLocale])

  if (typeof localizedMessage === 'string') {
    return localizedMessage
  }

  const fallbackMessage = readMessage(localeMessages.en)

  if (typeof fallbackMessage === 'string') {
    return fallbackMessage
  }

  return key
}

const translate = (key, placeholders = {}) => {
  const message = resolveTranslation(settingsStore.locale, key)

  if (typeof message !== 'string') {
    return key
  }

  return message.replace(/\{(\w+)\}/g, (_fullMatch, placeholder) => {
    if (Object.prototype.hasOwnProperty.call(placeholders, placeholder)) {
      return String(placeholders[placeholder])
    }

    return `{${placeholder}}`
  })
}

registerToastStore(toastsStore)

setApiHandlers({
  onUnauthorized: async (error, context) => {
    const currentRoute = router.currentRoute.value
    const isGuestRoute = Boolean(currentRoute?.meta?.isGuest)
    const isMeProbe = context.url === '/api/v1/me'

    if (context.url === '/logout') {
      router.replace({ name: 'Login' })
      return
    }

    if (isMeProbe && !authStore.user) {
      return
    }

    if (authStore.user) {
      await authStore.logout()
        .catch((logoutError) => {
          console.error('error in auth store logout', logoutError)
        })
    }

    if (isGuestRoute) {
      return
    }

    router.replace({ name: 'Login' })
  },
  onForbidden: async () => {
    await router.push({ name: 'VerifyEmail' })
  },
  onLocked: async (error, context) => {
    return await new Promise((resolve) => {
      let resolved = false

      const finish = (value) => {
        if (resolved) {
          return
        }

        resolved = true
        resolve(value)
      }

      modalStore.open({
        view: ConfirmPasswordModal,
        title: translate('app.confirmPasswordModal.title'),
        description: translate('app.confirmPasswordModal.description'),
        headerIcon: LockClosedIcon,
        buttons: [
          {
            text: translate('common.actions.cancel'),
            variant: 'secondary',
            onClick: () => {
              modalStore.close()
              finish(false)
            },
          },
          {
            text: translate('common.actions.confirm'),
            variant: 'primary',
            action: 'submit',
          },
        ],
        listeners: {
          confirmed: () => {
            finish(true)
            modalStore.close()
          },
        },
        props: {
          onConfirmedCallback: context.requestConfig?.onPasswordConfirmed ?? null,
        },
        onClose: () => {
          finish(false)
        },
      })
    })
  },
  onMaintenance: (error, context) => {
    const currentRoute = router.currentRoute.value
    if (currentRoute?.name !== 'Maintenance') {
      systemStore.setLastRoute(currentRoute.fullPath)
      router.replace({ name: 'Maintenance' })
    }

    systemStore.setMaintenance({
      status: context.status,
      message: context.message,
    })
  },
  onError: async (error, context) => {
    const ignoredUrls = ['/api/v1/me', '/logout', '/user/confirm-password']

    if (error?.code === 'ERR_CANCELED' || ignoredUrls.includes(context.url)) {
      return
    }

    const fieldErrors = getApiFieldErrors(error)
    const status = Number(context.status ?? 0)
    const hasValidationErrors = status === 422 && Object.keys(fieldErrors).length > 0

    if (hasValidationErrors) {
      return
    }

    const firstFieldKey = Object.keys(fieldErrors)[0]
    const message = firstFieldKey
      ? fieldErrors[firstFieldKey]
      : context.message ?? error?.message ?? translate('app.errors.somethingWentWrong')

    const title = status === 422
      ? translate('app.errors.validationFailed')
      : status >= 500
        ? translate('app.errors.serverError')
        : translate('app.errors.requestFailed')

    toast.error({
      title,
      message,
    })
  },
})

authStore.attemptUser()
  .catch((error) => {
    if (error.response?.status !== 401) {
      console.error(error)
    }
  })
  .finally(() => {
    settingsStore.initialize(authStore.user)
    setDateTimeLocale(settingsStore.locale)
    const i18n = createI18n({
      legacy: false,
      globalInjection: true,
      locale: settingsStore.locale,
      fallbackLocale: 'en',
      messages: {
        en,
        de,
      },
    })

    settingsStore.$subscribe((mutation, state) => {
      i18n.global.locale.value = state.locale
      setDateTimeLocale(state.locale)
    })

    const app = createApp(App)
      .use(pinia)
      .use(router)
      .use(i18n)
      .use(UiComponents);

    app.mount('#app')
  })
